import * as React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Sheet from "@mui/joy/Sheet";
import IconButton from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import NoDataComponent from "../Utils/NoData";
import PaginationTable from "../Pagination Tables/PaginationEventTable";
import Loading from "../Utils/Loading";
import { db } from "../../FirebaseConfig";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { UserAuth } from "../../context/AuthContext";

export default function EventTable() {
  const { userData } = UserAuth();
  const [open, setOpen] = React.useState(false);
  const [liveFilter, setLiveFilter] = React.useState("all");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  let [data, setData] = React.useState<any>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        let query = db.collection(
          "Events"
        ) as firebase.firestore.Query<firebase.firestore.DocumentData>;

        if (userData?.role === "organizer") {
          query = query.where("createdBy", "==", userData.id);
        }

        const querySnapshot = await query.get();
        const eventData = querySnapshot.docs.map((d: any) => ({
          id: d.id,
          ...d.data(),
        }));

        setData(eventData);
      } catch (err) {
        console.error("Error fetching events:", err);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [userData]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleLiveFilterChange = (event: any, value: string | null) => {
    setLiveFilter(value ?? "all");
  };

  const handleCategoryFilterChange = (event: any, value: string | null) => {
    setCategoryFilter(value ?? "all");
  };

  const filteredRows = data.filter((item: any) => {
    // Check if row matches the search query
    const matchesSearchQuery =
      searchQuery.length === 0 ||
      (item.name && item.name.toLowerCase().includes(searchQuery)) ||
      (item.category && item.category.toLowerCase().includes(searchQuery)) ||
      (item.eventdescription &&
        item.eventdescription.toLowerCase().includes(searchQuery)) ||
      (item.difficulty &&
        item.difficulty.toLowerCase().includes(searchQuery)) ||
      (item.points &&
        item.points.toString().toLowerCase().includes(searchQuery)) ||
      (item.startdate && item.startdate.toLowerCase().includes(searchQuery)) ||
      (item.enddate && item.enddate.toLowerCase().includes(searchQuery));

    // Match the live filter, considering boolean values and converting the filter value appropriately
    const matchesLiveFilter =
      liveFilter === "all" || item.live === (liveFilter === "true");

    const matchesCategoryFilter =
      categoryFilter === "all" || item.category === categoryFilter;

    // Return true if row matches all criteria
    return matchesSearchQuery && matchesCategoryFilter && matchesLiveFilter;
  });

  const getUniqueFilterOptions = (key: any) => {
    const allItems = data.map((item: any) => item[key]);
    const flatItems = [].concat(...allItems);
    const uniqueItems = Array.from(new Set(flatItems));
    return uniqueItems;
  };

  const renderFilters = () => {
    const liveOptions = getUniqueFilterOptions("live");
    const categoryOptions = getUniqueFilterOptions("category");

    return (
      <React.Fragment>
        <FormControl size="sm">
          <FormLabel>Category</FormLabel>
          <Select
            size="sm"
            value={categoryFilter}
            onChange={handleCategoryFilterChange}
            placeholder="All"
          >
            <Option value="all">All</Option>
            {categoryOptions.map((category) => (
              <Option key={category} value={category}>
                {category}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl size="sm">
          <FormLabel>Live</FormLabel>
          <Select
            size="sm"
            value={liveFilter}
            onChange={handleLiveFilterChange}
            placeholder="All"
          >
            <Option value="all">All</Option>
            <Option value="true">Live</Option>
            <Option value="false">Not Live</Option>
          </Select>
        </FormControl>
      </React.Fragment>
    );
  };

  return loading ? (
    <Loading />
  ) : (
    <React.Fragment>
      <Sheet
        className="SearchAndFilters-mobile"
        sx={{
          display: { xs: "flex", sm: "none" },
          my: 1,
          gap: 1,
        }}
      >
        <Input
          size="sm"
          placeholder="Search"
          startDecorator={<SearchIcon />}
          sx={{ flexGrow: 1 }}
          onChange={(event) => handleSearchChange(event)}
        />
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          onClick={() => setOpen(true)}
        >
          <FilterAltIcon />
        </IconButton>
        <Modal open={open} onClose={() => setOpen(false)}>
          <ModalDialog aria-labelledby="filter-modal" layout="fullscreen">
            <ModalClose />
            <Typography id="filter-modal" level="h2">
              Filters
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Sheet sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {renderFilters()}
              <Button color="primary" onClick={() => setOpen(false)}>
                Submit
              </Button>
            </Sheet>
          </ModalDialog>
        </Modal>
      </Sheet>
      <Box
        className="SearchAndFilters-tabletUp"
        sx={{
          borderRadius: "sm",
          py: 2,
          display: { xs: "none", sm: "flex" },
          flexWrap: "wrap",
          gap: 1.5,
          "& > *": {
            minWidth: { xs: "120px", md: "160px" },
          },
        }}
      >
        <FormControl sx={{ flex: 1 }} size="sm">
          <FormLabel>Search for Challenge</FormLabel>
          <Input
            size="sm"
            placeholder="Enter a keyword or phrase to search..."
            startDecorator={<SearchIcon />}
            onChange={(event) => handleSearchChange(event)} // Use the search input handler here
          />
        </FormControl>
        {renderFilters()}
      </Box>

      {data && data.length > 0 ? (
        <PaginationTable data={filteredRows} rowsPerPage={20} />
      ) : (
        <NoDataComponent />
      )}
    </React.Fragment>
  );
}
